import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import {
  CmsClient, activeTab, applyPersisted, buildReminders, headerFor, initialState,
  launchThemeId, nocturneBundle, reducer, resolveBundle, showsGridToggle, toPersisted,
  type PersistedState, type TenantBundle,
} from '@ce/core';
import { ThemeProvider } from './ThemeProvider';
import { Splash } from './components/Splash';
import { Header, TabBar } from './components/Chrome';
import { BookingSheet } from './components/BookingSheet';
import { useHistoryRouting } from './useHistoryRouting';
import { Onboarding } from './screens/Onboarding';
import { Home } from './screens/Home';
import { Schedule, Timeline } from './screens/Schedule';
import { Talent, TalentDetail } from './screens/Talent';
import { Events, Hall } from './screens/Hall';
import { Quests } from './screens/Quests';
import { Me, Cart } from './screens/Me';
import { Search } from './screens/Search';
import { SessionDetail } from './screens/SessionDetail';

const CMS_BASE = import.meta.env.VITE_CMS_URL ?? '';
const TENANT = import.meta.env.VITE_TENANT_ID ?? 'nocturne-con';
const PERSIST_KEY = `ce.state.${TENANT}`;

const webStorage = {
  async getItem(key: string) {
    try { return window.localStorage.getItem(key); } catch { return null; }
  },
  async setItem(key: string, value: string) {
    try { window.localStorage.setItem(key, value); } catch { /* private mode */ }
  },
};

export default function App() {
  // Start from the bundled sample so the very first paint has a theme; the CMS
  // result replaces it as soon as it lands.
  const [rawBundle, setRawBundle] = useState<TenantBundle>(nocturneBundle);
  const [loaded, setLoaded] = useState(!CMS_BASE);
  const [splashDone, setSplashDone] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  const [state, dispatch] = useReducer(reducer, rawBundle, (b) => {
    let persisted: PersistedState | null = null;
    try {
      const raw = window.localStorage.getItem(PERSIST_KEY);
      persisted = raw ? (JSON.parse(raw) as PersistedState) : null;
    } catch { /* ignore */ }
    const restored = applyPersisted(initialState(b), persisted);
    // Decide the launch look once, during init, so the first paint is already
    // the right theme — resolving it in an effect would flash the default.
    return { ...restored, themeId: launchThemeId(b, persisted?.themeId ?? null, restored.themeShuffle) };
  });

  // Theme resolves from the raw bundle plus the attendee's chosen variant, so
  // picking a theme repaints without refetching or remounting anything.
  const { bundle, theme } = useMemo(
    () => resolveBundle(rawBundle, state.themeId),
    [rawBundle, state.themeId],
  );

  // Bundle load: cache-first, revalidating in the background.
  useEffect(() => {
    if (!CMS_BASE) return;
    let cancelled = false;
    const client = new CmsClient({
      baseUrl: CMS_BASE,
      tenantId: TENANT,
      storage: webStorage,
      fallback: nocturneBundle,
    });
    client
      .load((fresh) => { if (!cancelled) setRawBundle(fresh.bundle); })
      .then((first) => {
        if (cancelled) return;
        setRawBundle(first.bundle);
        setLoaded(true);
      })
      .catch(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Persist the attendee's own data. Content is never persisted here — that is
  // the CMS cache's job, and mixing them causes stale-content bugs.
  useEffect(() => {
    try {
      window.localStorage.setItem(PERSIST_KEY, JSON.stringify(toPersisted(state)));
    } catch { /* ignore */ }
  }, [state]);

  // Local reminders for starred sessions, via the Notifications API when the
  // attendee has granted it. Falls back silently — a missing permission is not
  // an error worth interrupting anyone over.
  useEffect(() => {
    if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return;
    const reminders = buildReminders(bundle, state.saved);
    const timers = reminders
      .filter((r) => r.fireAt - Date.now() < 24 * 60 * 60 * 1000)
      .map((r) =>
        window.setTimeout(
          () => new Notification(r.title, { body: r.body, tag: r.id }),
          Math.max(0, r.fireAt - Date.now()),
        ),
      );
    return () => timers.forEach(clearTimeout);
  }, [bundle, state.saved]);

  useHistoryRouting(state, dispatch);

  // A new screen starts at the top. Without this, opening a session from the
  // bottom of the schedule drops you into the middle of its detail page.
  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [state.screen, state.talentId, state.sessionId]);

  const header = useMemo(() => headerFor(bundle, state), [bundle, state]);
  const onSplashDone = useCallback(() => setSplashDone(true), []);

  const screenProps = { bundle, state, dispatch };

  return (
    <ThemeProvider theme={theme}>
      <div className="ce-stage">
        <div className="ce-device">
          {!splashDone && (
            <Splash theme={theme} ready={loaded} onDone={onSplashDone} />
          )}

          {state.screen === 'onboard' ? (
            <Onboarding {...screenProps} />
          ) : (
            <>
              <Header
                kicker={header.kicker}
                title={header.title}
                cartCount={state.cart.length}
                showGrid={showsGridToggle(state.screen)}
                onSearch={() => dispatch({ type: 'go', screen: 'search' })}
                onGrid={() =>
                  dispatch({
                    type: 'go',
                    screen: state.screen === 'timeline' ? 'schedule' : 'timeline',
                  })
                }
                onCart={() => dispatch({ type: 'go', screen: 'cart' })}
              />

              <main ref={mainRef} className="scroll" style={{ flex: 1, overflow: 'auto', padding: '4px 20px 0' }}>
                {state.screen === 'home' && <Home {...screenProps} />}
                {state.screen === 'schedule' && <Schedule {...screenProps} />}
                {state.screen === 'timeline' && <Timeline {...screenProps} />}
                {state.screen === 'talent' && <Talent {...screenProps} />}
                {state.screen === 'talentDetail' && <TalentDetail {...screenProps} />}
                {state.screen === 'events' && <Events {...screenProps} />}
                {state.screen === 'hall' && <Hall {...screenProps} />}
                {state.screen === 'quests' && <Quests {...screenProps} />}
                {state.screen === 'me' && <Me {...screenProps} />}
                {state.screen === 'cart' && <Cart {...screenProps} />}
                {state.screen === 'search' && <Search {...screenProps} />}
                {state.screen === 'sessionDetail' && <SessionDetail {...screenProps} />}
              </main>

              <TabBar
                active={activeTab(state.screen)}
                onGo={(key) => dispatch({ type: 'go', screen: key as never })}
              />
            </>
          )}

          <BookingSheet bundle={bundle} state={state} dispatch={dispatch} />
        </div>
      </div>
    </ThemeProvider>
  );
}
